// SubscriptionPeriod.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSubscriptionPaths, currentLang } from '../../../utils/functions';
import { localStorageUtil } from '../../../utils/functions';
import W from '../../../components/common/Word';
import { useTranslation } from 'react-i18next';
import parse from 'html-react-parser';
import flatstore from 'flatstore';
import {
  getSubscriptionProductList
} from "../../../utils/services";
import {isNil} from 'lodash';
import Price from '../../../components/product/common/Price';


function SubscriptionPeriod({subscription}) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const paths = getSubscriptionPaths();
    const type = 'customize';
    const size = localStorageUtil.getItem("subscription")?.size || "";
    const items = localStorageUtil.getItem("subscription")?.items || {};

    // Simplified state initialization
    const [period, setPeriod] = useState(localStorageUtil.getItem('subscription')?.period || '2_week');

    const [submissionError, setSubmissionError] = useState('');

    const isResetting = sessionStorage.getItem("resetting_subscription") === "true";
    const [loading, setLoading] = useState(false); // State to track loading state

    const [data, setData] = useState([]);

    useEffect(() => {
        if (!items || Object.keys(items).length === 0) {
            navigate(paths.steps['customize'] || '/');
        } else if( ! size ){
            navigate(paths.base || '/');
        }
    }, []);

    useEffect(() => {
        if (isNil(subscription)) {
            getSubscriptionProductList();
        }
        if (subscription && !isNil(subscription)) {
            setData(subscription);
        }
    }, [subscription]);

    const handleSubmit = () => {
        if (period) {
            // If not loading, proceed with the AJAX request
            if (!loading) {
                // Check if we're resetting a subscription
                const isResetting =
                sessionStorage.getItem("resetting_subscription") === "true";

                setLoading(true); // Set loading state to true
                flatstore.set("basket_status", true);

                const formData = new FormData();
                formData.append("type", type);
                formData.append("size", size);
                formData.append("period", period);
                formData.append("curated", "");
                formData.append("items", JSON.stringify(items));

                if (!isResetting) {
                    fetch(`/${currentLang()}/shop/wp-json/grutto/v2/subscription/add-to-cart`, {
                        method: "POST",
                        body: formData,
                    })
                        .then((response) => response.json())
                        .then((data) => {
                            if (data.status === "success" && data.items) {
                                flatstore.set("basket_count", data.count);
                                flatstore.set("basket_status", false);

                                // Reset all subscription data from localStorage
                                localStorageUtil.removeItem("subscription");

                                window.location.href = "/" + currentLang() + "/shop/";
                            } else {
                                setLoading(false);
                                setSubmissionError(data?.message || "Failed to add subscription");
                                flatstore.set("basket_status", false);
                            }
                        })
                        .catch((error) => {
                            setLoading(false);
                            setSubmissionError("Error adding bundle to cart");

                            console.error("Error adding bundle to cart:", error);
                        });
                } else {
                    fetch(`/${currentLang()}/shop/wp-json/grutto/v2/subscription/reset`, {
                        method: "POST",
                        body: formData,
                        headers: {
                        "X-WP-Nonce": sessionStorage.getItem("resetting_subscription_nonce"),
                        },
                        credentials: "include",
                    })
                    .then((response) => response.json())
                    .then((data) => {
                    // in the case of subscription rest, the success result would be this
                    // { "success": true, "message": "Subscription updated successfully", "subscription_id": 636476 }
                    if (data.success && data.subscription_id) {
                        // flatstore.set("basket_count", data.data.cart_data);
                        flatstore.set("basket_status", false);
                        
                        // Reset all subscription data from localStorage
                        localStorageUtil.removeItem("subscription");
                        
                        // Clear all resetting-related sessionStorage
                        sessionStorage.removeItem("resetting_subscription");
                        sessionStorage.removeItem("resetting_subscription_nonce");
                        
                        window.location.href =
                        "/" + currentLang() + "/shop/my-account/subscriptions/";
                    } else {
                        setLoading(false);
                        setSubmissionError(data.data.message);
                        flatstore.set("basket_status", false);
                    }
                    })
                    .catch((error) => {
                        setLoading(false);
                        setSubmissionError('Error adding bundle to cart');
                        console.error("Error adding bundle to cart:", error);
                    });
                }
                setSubmissionError('');
            }
        } else {
            setSubmissionError('Please select both a period for your subscription.');
        }
    };

    const handlePeriodChange = (e) => setPeriod(e.target.value);

    // Update progress based on selections
    let progressBarWidthPercentage = 75;
    if (period) {
        progressBarWidthPercentage = 100;
    }

    // ProgressBar
    const progressBarWidth = {
        width: `${progressBarWidthPercentage}%`,
    };

    const getPrice = (amount, unit, sizeOverride) => {

        let price = 0;

        if(!data.length){
            return price;
        }

        // Determine the subscription size to use
        const effectiveSize = sizeOverride || size;

        // Define the subscription type pattern based on the effective type and size
        let subscriptionTypePattern;
        if (type === 'customize') {
            subscriptionTypePattern = new RegExp(`^grutto_subscription_custom_${effectiveSize}$`);
        } else if (type === 'curated') {
            subscriptionTypePattern = new RegExp(`^grutto_subscription_curated_.+_${effectiveSize}$`);
        }

        const subscriptionItem = data.find(item => subscriptionTypePattern.test(item.subscription_type));

        if (subscriptionItem) {
            const bundlePrice = parseFloat(subscriptionItem.bundle_price);

            if(sizeOverride){
                return bundlePrice.toFixed(2)
            }

            let weekCount;
            if (unit === 'week') {
                weekCount = amount;
            } else {
                weekCount = amount * (52/12); // (months) * (52 weeks / 12 months)
            }

            price = (bundlePrice / weekCount).toFixed(2);
        }

        return price;
    };

    return (
        <div className="subscription-box">

            <div className='progress-bar'>
                <div className='fill' style={progressBarWidth}></div>
            </div>

            {submissionError && (
                <div className="response error">
                {submissionError}
                </div>
            )}

            <div className='container'>
                <div className='row justify-content-center'>
                    <div className='col-md-10'>
                        <div className="container py-4">
                            <div className="row">
                                <div className="col primary-title">
                                    <h2><W>subscription.period.title</W></h2>
                                    <p><W>subscription.period.subtitle</W></p>
                                </div>
                            </div>

                            <div className="row justify-content-center">
                                {['2_week', '4_week', '6_week', '2_month'].map(periodOption => {
                                    let [amount, unit] = periodOption.split("_");
                                    // Check if the periodOption is "2_month" and update the display
                                    if (periodOption === '2_month') {
                                        amount = '8';
                                        unit = 'week';
                                    }
                                    return (
                                        <div className="col-6 col-sm-3" key={periodOption}>
                                            <label className="subscription__select">
                                                <input type="radio" name="period" value={periodOption} checked={period === periodOption} onChange={handlePeriodChange} />
                                                <div className="checkbox">
                                                    <span></span>
                                                </div>
                                                <div className="body">
                                                    <div className="title">
                                                        <span className="subtitle"><W>subscription.each</W></span>
                                                        <h3 className="heading">{amount} {<W>subscription.{unit}</W>}</h3>
                                                    </div>
                                                    <div className="s-price">
                                                        <span className="before"><W>subscription.only</W></span>
                                                        <span className="amount"><Price value={getPrice(amount,unit)} withCurrency={1} /></span>
                                                        <span className="after"><W>subscription.perweek</W></span>
                                                    </div>
                                                </div>
                                            </label>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="subscription__cta">
                {loading ? (
                  <button className="btn btn--loading">
                    <span className="ico"></span>
                  </button>
                ) : period ? (
                  <button className="btn btn--red" onClick={handleSubmit}>
                    {isResetting ? (
                      <W>subscription.continue_delivery_reset</W>
                    ) : (
                      <W>subscription.continue_delivery</W>
                    )}
                  </button>
                ) : (
                  <button className="btn btn--disable">
                    {" "}
                    {isResetting ? (
                      <W>subscription.continue_delivery_reset</W>
                    ) : (
                      <W>subscription.continue_delivery</W>
                    )}{" "}
                  </button>
                )}
            </div>
        </div>
    );
}

export default flatstore.connect(['subscription'])(SubscriptionPeriod);